import React, { useContext, useEffect, useMemo, useState } from 'react';
import { CountryCode } from '@dayetopia/types';
import { useLocation } from '@reach/router';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import AddToCart from '@components/shared/AddToCart';
import Button from '@components/shared/Button';
import Colors from '@components/shared/Colors';
import TextWithButtonBox from '@components/shared/TextWithButtonBox';
import TwoColumn from '@components/shared/TwoColumn';
import { SFDBodyLG } from '@components/shared/Typography';
import useDesktop from '@components/shared/useDesktop';
import { ButtonTypes, CartItem, PageSlice, Prices, ProductCode, TextWithButtonData } from '@contracts';
import Tick from '@images/sampler/tick.svg';
import { getMinPrice } from '@utils/boxPrices';
import { getPriceText, getProductPrice } from '@utils/price';
import useNavigate from '@utils/useNavigate';

interface Props {
  slice: PageSlice;
}

const SamplerSubscriptionModule: React.FC<Props> = ({ slice }) => {
  const { t } = useTranslation();
  const { navigateWrapper } = useNavigate();
  const location = useLocation();
  const { state } = useContext(Context);
  const { currency, country } = state;
  const [prices, setPrices] = useState<Prices>({});
  const image = getImage(slice.primary?.image);
  const imageAlt = slice.primary?.image?.alt ?? '';
  const features = slice.items ?? [];
  const backgroundColor = slice.primary.background_color ?? '';
  const textColor = slice.primary.text_color ?? '';
  const isDesktop = useDesktop(1024);
  const minTamponBoxQuantity = country === CountryCode.US ? 18 : 12;
  const finalButtonsData = useMemo(() => {
    if (slice?.primary?.buttons_data) {
      return slice.primary.buttons_data;
    }

    const buttonsData = [
      {
        description: t('subscription-module.tampon-box', { count: minTamponBoxQuantity }),
        buttonText: t('components.choose-box'),
        onClick: () => {
          if (location.pathname.endsWith('/products/tampons/')) {
            window.scrollTo({ top: isDesktop ? 80 : 670, behavior: 'smooth' });
          } else {
            navigateWrapper('/products/tampons/');
          }
        },
        productCode: ProductCode.TamponBox,
        backgroundColor: '#d16aa9',
        type: ButtonTypes.Default
      }
    ];

    return buttonsData;
  }, []);

  const handleFetchProductPrice = async () => {
    finalButtonsData.map(async (button: TextWithButtonData) => {
      if (button.productCode === ProductCode.TamponBox) {
        if (country === CountryCode.BG) {
          const productPrice = await getMinPrice(ProductCode.TamponBox9CR, currency, country);
          if (!productPrice) return;
          setPrices((prevState) => ({ ...prevState, [button.productCode]: productPrice.price }));
        } else {
          const productPrice = await getMinPrice(button.productCode, currency, country);
          if (!productPrice) return;
          setPrices((prevState) => ({ ...prevState, [button.productCode]: productPrice.price }));
        }
      } else {
        const getPrice = await getProductPrice(button.productCode, currency, country);
        if (!getPrice) return;
        setPrices((prevState) => ({ ...prevState, [button.productCode]: getPrice.price }));
      }
    });
  };

  const getButton = (buttonData: TextWithButtonData) => {
    switch (buttonData.type) {
      case ButtonTypes.AddToCart:
        return (
          <AddToCart
            cartItem={new CartItem(buttonData.productCode, buttonData.productCode, prices[buttonData.productCode])}
            backgroundColor={Colors.paleGold}
            buttonTextColor={Colors.forestGreen}
          />
        );
      default:
        return (
          <ButtonStyled onClick={buttonData.onClick} $backgroundColor={buttonData.backgroundColor}>
            {buttonData.buttonText.toUpperCase()}
          </ButtonStyled>
        );
    }
  };

  useEffect(() => {
    handleFetchProductPrice();
  }, []);

  return (
    <Container $background={backgroundColor} $textColor={textColor}>
      <TwoColumn>
        {[
          <ImageWrap key={0}>
            {image ? <GatsbyImage image={image} alt={imageAlt} /> : <img src={slice.primary?.image?.url} alt={imageAlt} />}
          </ImageWrap>,
          <TextWrap key={1}>
            <TitleWrap>{slice?.primary?.title?.text}</TitleWrap>
            {slice?.primary?.description?.text ? (
              <DescriptionWrapper>
                <SFDBodyLG>{slice?.primary?.description?.text}</SFDBodyLG>
              </DescriptionWrapper>
            ) : null}
            <TicksTitle>{slice?.primary?.ticks_title?.text}</TicksTitle>
            <TicksWrap>
              {features.map((item, index) => {
                const { text } = item.feature;
                if (text === 'Free delivery in the UK' && country !== CountryCode.GB) {
                  return null;
                }
                return (
                  <Row key={index}>
                    <Tick style={{ marginRight: '14px' }} />
                    {text}
                  </Row>
                );
              })}
            </TicksWrap>
            <ButtonsWrap>
              {finalButtonsData.map((button, index) => (
                <TextWithButtonBox
                  key={index}
                  data={button}
                  price={`<b>${getPriceText(prices[button.productCode] || 6.0, country, currency, t).fullString}</b>`}
                  button={getButton(button)}
                />
              ))}
            </ButtonsWrap>
          </TextWrap>
        ]}
      </TwoColumn>
    </Container>
  );
};

export default SamplerSubscriptionModule;

interface ContainerProps {
  $background?: string;
  $textColor?: string;
}

const Container = styled.div<ContainerProps>`
  height: auto;
  background: ${(props) => (props.$background ? props.$background : 'linear-gradient(180deg, #FBE5B1 0%, #F9D07C 100%)')};
  color: ${(props) => (props.$textColor ? props.$textColor : Colors.forestGreen)};

  @media (max-width: 1024px) {
    width: 100%;
  }
`;

const ImageWrap = styled.div`
  max-width: 692px;
  height: auto;

  @media (max-width: 1024px) {
    max-width: 100%;
    align-self: center;
  }
`;

const TextWrap = styled.div`
  max-width: 580px;
  margin-right: 120px;
  margin-left: 40px;
  margin-top: 90px;
  margin-bottom: 70px;

  @media (max-width: 768px) {
    margin: 30px 0;
    max-width: 320px;
    align-self: center;
  }
`;

const TitleWrap = styled.div`
  margin-bottom: 20px;

  font-family: Domaine;
  font-size: 30px;
  font-style: normal;
  font-weight: 500;
  line-height: 36px;
  text-align: left;

  @media (max-width: 1024px) {
    font-size: 24px;
    line-height: 29px;
    letter-spacing: -0.02em;
    text-align: center;
  }
`;

const DescriptionWrapper = styled.div`
  margin-bottom: 15px;
  max-width: 430px;
`;

const TicksWrap = styled.div`
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    margin-bottom: 10px;
  }
`;

const Row = styled.div`
  font-family: BrownStd;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 27px;
  letter-spacing: 0;
  text-align: left;

  @media (max-width: 1024px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const ButtonsWrap = styled.div`
  @media (max-width: 1024px) {
    width: 100%;
  }
`;

interface ButtonStyledProps {
  $backgroundColor?: string;
}

const ButtonStyled = styled(Button)<ButtonStyledProps>`
  all: unset;
  cursor: pointer;
  transition: all 0.2s ease;

  color: ${Colors.cream};
  width: 100%;
  height: 100%;
  background: ${(props) => props.$backgroundColor};
  border-radius: 2px;

  font-family: BrownStd;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 1px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const TicksTitle = styled(Row)`
  font-weight: 700;
  margin-bottom: 5px;
`;
